"use client";

import { useState, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { Fingerprint } from "lucide-react";
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";

// Inloggen en koppelen met een passkey: Face ID, Touch ID of de pincode van
// het toestel. Zie server/passkeys.ts en de routes onder /api/leden/passkey.

const nooit = () => () => {};
function useKanPasskeys(): boolean {
  return useSyncExternalStore(nooit, () => browserSupportsWebAuthn(), () => false);
}

async function stap(pad: string, body: object) {
  const res = await fetch(pad, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return { ok: res.ok, data: await res.json().catch(() => ({})) };
}

// Wegtikken of niets kiezen is geen fout; dan zeggen we niets.
const weggetikt = (e: unknown) => e instanceof Error && (e.name === "NotAllowedError" || e.name === "AbortError");

export function PasskeyInloggen() {
  const kan = useKanPasskeys();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  if (!kan) return null;

  async function inloggen() {
    setFout(null);
    setBezig(true);
    try {
      const opties = await stap("/api/leden/passkey/inloggen", { stap: "opties" });
      const antwoord = await startAuthentication({ optionsJSON: opties.data });
      const uitkomst = await stap("/api/leden/passkey/inloggen", { stap: "bevestig", antwoord });
      if (uitkomst.ok) window.location.href = "/leden";
      else setFout("Inloggen met dit toestel lukte niet. Log in met je lidnummer en koppel het opnieuw.");
    } catch (e) {
      if (!weggetikt(e)) setFout("Inloggen met dit toestel lukte niet. Log in met je lidnummer.");
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={inloggen}
        disabled={bezig}
        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary px-4 py-3 font-display font-black uppercase tracking-wide text-primary disabled:opacity-60"
      >
        <Fingerprint className="size-5" aria-hidden />
        {bezig ? "Bezig..." : "Inloggen met Face ID"}
      </button>
      <p className="text-center text-xs text-muted-foreground">
        Of Touch ID, of de pincode van je toestel. Eerst één keer koppelen, na inloggen met je lidnummer.
      </p>
      {fout && <p className="text-sm text-destructive">{fout}</p>}
    </div>
  );
}

export function PasskeyKoppelen({ heeftAl }: { heeftAl: boolean }) {
  const kan = useKanPasskeys();
  const router = useRouter();
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);
  if (!kan) {
    return <p className="text-xs text-muted-foreground">Dit toestel of deze browser kan geen passkeys.</p>;
  }

  async function koppelen() {
    setMelding(null);
    setBezig(true);
    try {
      const opties = await stap("/api/leden/passkey/koppelen", { stap: "opties" });
      if (!opties.ok) throw new Error("opties");
      const antwoord = await startRegistration({ optionsJSON: opties.data });
      const uitkomst = await stap("/api/leden/passkey/koppelen", { stap: "bevestig", antwoord });
      if (uitkomst.ok) {
        setMelding("Gekoppeld. Voortaan log je op dit toestel in met Face ID, Touch ID of je pincode.");
        router.refresh();
      } else setMelding("Koppelen lukte niet. Probeer het nog eens.");
    } catch (e) {
      // InvalidStateError: dit toestel is al gekoppeld.
      if (e instanceof Error && e.name === "InvalidStateError") setMelding("Dit toestel is al gekoppeld.");
      else if (!weggetikt(e)) setMelding("Koppelen lukte niet. Probeer het nog eens.");
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={koppelen}
        disabled={bezig}
        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary px-4 py-3 text-sm font-semibold text-primary disabled:opacity-60"
      >
        <Fingerprint className="size-5" aria-hidden />
        {bezig ? "Bezig..." : heeftAl ? "Nog een toestel koppelen" : "Inloggen met Face ID instellen"}
      </button>
      {melding && <p className="text-sm text-soft-foreground">{melding}</p>}
    </div>
  );
}
